import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { formatDateString } from "../../../utils/dates";
import { useOutsideAlerter } from "../../../utils/outsideAlerter";
import { Icon } from "../../Common/Icons/LucideIcons";
import {
  calendarStyles,
  DatepickerButton,
  DatepickerInput,
  DatepickerWrapper,
} from "./__styles__/Datepicker";

export const parseValue = (value: Maybe<string | Date>): Date | null => {
  if (!value) return null;

  if (value instanceof Date) return value;

  return moment(moment.utc(value).format("YYYY-MM-DD")).toDate();
};

export function toString(val: Maybe<Date>) {
  if (!val) return "";

  return formatDateString({
    format: "YYYY-MM-DD",
    dateString: val.toUTCString(),
    timeZone: "UTC",
  });
}

export type DatepickerCoreProps = {
  id?: string;
  value: Maybe<string | Date>;
  label?: string;
  onChange: (value: string) => void;
  name?: string;
  size?: "smaller" | "small" | "medium" | "large";
  error?: boolean;
  readonly?: boolean;
} & Pick<
  ReactDatePickerProps,
  | "tabIndex"
  | "dateFormat"
  | "isClearable"
  | "showMonthDropdown"
  | "showYearDropdown"
  | "showTimeSelect"
  | "timeFormat"
  | "timeIntervals"
  | "excludeDates"
  | "includeDates"
  | "filterDate"
  | "onBlur"
  | "onFocus"
  | "autoComplete"
  | "autoFocus"
  | "maxDate"
  | "minDate"
  | "placeholderText"
  | "disabled"
  | "required"
>;

/**
 * Not for external use. Use Datepicker or ReactHookFormDatepicker instead.
 */
export const DatepickerCore = ({
  id,
  name,
  value,
  label,
  error,
  onChange,
  size = "medium",
  required,
  minDate,
  maxDate,
  disabled,
  readonly,
  ...props
}: DatepickerCoreProps) => {
  calendarStyles();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const toggleCalendarOpen = () => setIsCalendarOpen(open => !open);

  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => setIsCalendarOpen(false),
  });

  const handleDatepickerChange = (date: Maybe<Date>) => {
    onChange(toString(date));
  };

  const handleButtonPress = (event: any) => {
    event.preventDefault();
    toggleCalendarOpen();
  };

  // NOTE: We want to focus an actionable cell when the calendar is opened. This
  // is either the currently selected date or today's date. Since
  // react-datepicker does not provide a simple API to do so, we're using class
  // (BEM) and aria selectors to do so.
  useEffect(() => {
    // Brief timeout to ensure react-datepicker has rendered the calendar.
    const focusCalendarTimeout = setTimeout(() => {
      if (isCalendarOpen && wrapperRef.current) {
        const calendarContainer = wrapperRef.current.querySelector(
          ".react-datepicker__month-container",
        );

        const currentlySelectedCell = calendarContainer?.querySelector(
          '[aria-selected="true"]',
        ) as HTMLElement;

        const todaysDate = new Date().getDate();
        const todaysDateString = todaysDate.toString().padStart(3, "0");

        const todayCell = calendarContainer?.querySelector(
          `.react-datepicker__day--${todaysDateString}`,
        ) as HTMLElement;

        if (currentlySelectedCell) {
          currentlySelectedCell.focus();
        } else if (todayCell) {
          todayCell.focus();
        }
      }
    }, 100);

    return () => clearTimeout(focusCalendarTimeout);
  }, [isCalendarOpen]);

  return (
    <DatepickerWrapper
      ref={wrapperRef}
      size={size}
      error={!!error}
      onKeyDownCapture={event => {
        if (
          (event.key === "ArrowDown" || event.key === "ArrowUp") &&
          !isCalendarOpen
        ) {
          event.stopPropagation();
          event.preventDefault();
          setIsCalendarOpen(true);
        }
      }}
    >
      <DatePicker
        id={id}
        name={name}
        selected={parseValue(value)}
        preventOpenOnFocus={true}
        shouldCloseOnSelect={true}
        open={isCalendarOpen}
        onChange={handleDatepickerChange}
        onKeyDown={event => {
          if (event.key === "Escape") {
            setIsCalendarOpen(false);
          }
        }}
        onSelect={() => setIsCalendarOpen(false)}
        customInput={
          <DatepickerInput readOnly={readonly} disabled={disabled} />
        }
        maxDate={maxDate ?? new Date("12/31/9999")}
        // The default is effectively no minimum; however, the date picker doesn't like it when I set it to the year 0
        minDate={minDate ?? new Date("01/01/1000")}
        disabled={disabled || readonly}
        required={required}
        {...props}
      />
      <DatepickerButton
        onClick={handleButtonPress}
        disabled={disabled || readonly}
        type="button"
        aria-label={"Open calendar" + (label ? `, ${label}` : "")}
      >
        <Icon iconName="calendar" color="contentSecondary" size={16} />
      </DatepickerButton>
    </DatepickerWrapper>
  );
};
